<template>
  <a class="skip-to-content" href="#main">Vai al contenuto</a>
  <Sprite />
  <SpriteLD />
  <h1 class="h6 visually-hidden" role="banner">Sportello Online Regione Liguria</h1>
  <div class="dimmable">
    <LoadingPage />
    <AuthenticableHeader />
    <router-view id="main" />
    <Footer id="footer" :version="version" :show-version="versionShow" codice-tipologia-procedimento="PE"/>
    <Notification />
    <CustomModal :modal="modal" @hidden="closeModal" />
  </div>
</template>

<style>
@import "assets/style.scss";

.skip-to-content {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: -1;
  padding-inline: 1.75ch;
  padding-block: .75ch;
  border-radius: .5ch;
  background: white;
  color: black;
  font-weight: bold;
  opacity: 0;
}

.skip-to-content:focus-visible {
  outline-color: white;
  outline-style: solid;
  outline-width: 2px;
  outline-offset: 3px;
  opacity: 1;
  z-index: 999;
}
</style>
<script lang="ts" setup>
import AuthenticableHeader from "@/layouts/common/AuthenticableHeader.vue";
import { Footer, Sprite, SpriteLD } from "cdp-fe-commons-layout";
import LoadingPage from "@/components/general/LoadingPage.vue";
import Notification from "@/components/general/Notification.vue";
import { EVENTS, subscribeEvent } from "@/emitter_mitt";
import CustomModal from "@/components/general/CustomModal.vue";
import { ref, Ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { IModal } from "@/interfaces/components/general/IModal";
import { ModalDimension } from "@/enums/components/general/ModalDimension";
import { ICustomComponent } from "@/interfaces/components/common/ICustomComponent";
import SetEmail from "@/components/user/SetEmail.vue";
import { useNotificationStore } from "@/stores/notification";
import { NotificationStatus } from "@/enums/components/general/NotificationStatus";
import { NotificationPosition } from "@/enums/components/general/NotificationPosition";
import { INotification } from "@/interfaces/components/general/INotification";

const version: string = process.env.VUE_APP_VERSION;
const versionShow: boolean = process.env.VUE_APP_SHOW_VERSION === "true";

const router = useRouter();
const notificationStore = useNotificationStore();

// Lettura sincrona: cattura il fragment prima che Vue Router lo usi
const initialAuthError = (() => {
  const hash = window.location.hash;
  if (!hash) {
    return null;
  }
  const params = new URLSearchParams(hash.substring(1));
  return params.get('auth_error') ?? null;
})();

// Pulizia del fragment dopo che Vue Router ha completato la navigazione iniziale
if (initialAuthError) {
  const unregister = router.afterEach(() => {
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    unregister();
  });
}

onMounted(() => {
  if (initialAuthError) {
    notificationStore.addNotification({
      title: 'Accesso non riuscito',
      message: initialAuthError,
      status: NotificationStatus.ERROR,
      position: NotificationPosition.TOP,
      dismissable: true,
    } as INotification);
  }
});

const modal: Ref<IModal> = ref<IModal>({
  title: "Conferma l'email per le notifiche",
  closable: false,
  staticBackdrop: true,
  dimension: ModalDimension.MEDIUM,
  content: {
    render: SetEmail,
    content: { emailIniziale: null },
    events: {
      close: closeModal
    }
  } as ICustomComponent
} as IModal);

subscribeEvent(EVENTS.SET_EMAIL, openModal);

/**
 * Handler idempotente: una seconda emissione di SET_EMAIL a modale già aperta
 * non deve chiuderla né aggiornarne le props.
 */
function openModal(emailAlt?: string | null) {
  if (modal.value.show) {
    return;
  }
  if (modal.value.content) {
    modal.value.content.content = { emailIniziale: emailAlt ?? null };
  }
  modal.value.show = true;
}

function closeModal() {
  modal.value.show = false;
}

</script>
<style>
.btn-success {
  background: var(--cdp-color-success-btn) !important;
}

.btn-primary {
  color: white;
  background: var(--cdp-color-primary-btn) !important;
}

.btn-dropdown {
  color: var(--cdp-color-primary-btn) !important;
}

.icon.icon-primary {
  fill: var(--cdp-color-primary-btn) !important;
}

.dropdown-item.list-item > span {
  color: var(--cdp-color-primary-btn) !important;
}

.btn-outline-primary {
  color: var(--cdp-color-primary-btn) !important;
  box-shadow: inset 0 0 0 2px var(--cdp-color-primary-btn) !important;
}

.pagination .page-item .page-link[aria-current] {
  border: 1px solid var(--cdp-color-primary-btn) !important;
  color: var(--cdp-color-primary-btn) !important;
}
</style>
